import React, { useCallback } from "react"
import { Box, Grid, Typography, Button, TextField } from "@material-ui/core"

import useStyles from "@/pages/Attendance/ReferralPopup/screens/ReferralFormScreen/styles"
import { channelToLabel } from "@/pages/Attendance/ReferralPopup/constants"
import {
	ReferralFormData,
	ReferralChannel,
	ReferralPayoutMethod,
	ReferralFormScreenProps
} from "@/pages/Attendance/ReferralPopup/types"
import { keepOnlyDigits } from "@/utils/phone"

const payoutMethodToLabel: Record<ReferralPayoutMethod, string> = {
	cash: "Em dinheiro",
	invoice_discount: "Desconto na fatura"
}

const FORM_FIELD_TO_VALIDATION_KEY: Partial<Record<keyof ReferralFormData, string>> = {
	companyName: "businessName"
}

const ReferralFormScreen: React.FC<ReferralFormScreenProps> = (props) => {
	const {
		isAdmin,
		formData,
		onFormDataChange,
		onGoToLink,
		onSubmit,
		isSubmitting,
		apiValidationErrors,
		clearValidation
	} = props
	const classes = useStyles()

	const phoneInputRootClassName = apiValidationErrors?.whatsapp
		? `${classes.inputRoot} ${classes.inputRootError}`
		: classes.inputRoot

	const handleFormFieldChange = useCallback(<TField extends keyof ReferralFormData>(
		field: TField,
		value: ReferralFormData[TField]
	): void => {
		const validationKey = FORM_FIELD_TO_VALIDATION_KEY[field] ?? field
		clearValidation(validationKey)
		onFormDataChange({ [field]: value } as Partial<ReferralFormData>)
	}, [clearValidation, onFormDataChange])

	const handleDontRememberClick = useCallback(() => {
		onGoToLink()
	}, [onGoToLink])

	const handleSubmitClick = useCallback(() => {
		if (isSubmitting) {
			return
		}
		onSubmit()
	}, [isSubmitting, onSubmit])

	const renderChannelOption = (channel: ReferralChannel): React.ReactNode => {
		const isSelected = formData.channel === channel
		const cardClassName = isSelected
			? `${classes.optionCard} ${classes.optionCardSelected}`
			: classes.optionCard
		const radioClassName = isSelected
			? `${classes.radioOuter} ${classes.radioOuterSelected}`
			: classes.radioOuter

		return (
			<Box
				className={cardClassName}
				onClick={() => handleFormFieldChange("channel", channel)}
			>
				<Box className={radioClassName}>
					{isSelected && <Box className={classes.radioInner} />}
				</Box>
				<Typography className={classes.optionLabel}>
					{channelToLabel[channel]}
				</Typography>
			</Box>
		)
	}

	const renderPayoutMethodOption = (payoutMethod: ReferralPayoutMethod): React.ReactNode => {
		const isSelected = formData.payoutMethod === payoutMethod
		const cardClassName = isSelected
			? `${classes.optionCard} ${classes.optionCardSelected}`
			: classes.optionCard
		const radioClassName = isSelected
			? `${classes.radioOuter} ${classes.radioOuterSelected}`
			: classes.radioOuter

		return (
			<Box
				className={cardClassName}
				onClick={() => handleFormFieldChange("payoutMethod", payoutMethod)}
			>
				<Box className={radioClassName}>
					{isSelected && <Box className={classes.radioInner} />}
				</Box>
				<Typography className={classes.optionLabel}>
					{payoutMethodToLabel[payoutMethod]}
				</Typography>
			</Box>
		)
	}

	const renderAdminForm = (): React.ReactNode => {
		return (
			<React.Fragment>
				<Typography className={classes.fieldLabel}>
					{"Qual empresa você conhece nessa situação?"}
				</Typography>
				<Box className={classes.fieldsGrid}>
					<TextField
						type="text"
						value={formData.companyName}
						onChange={(event) => handleFormFieldChange("companyName", event.target.value)}
						placeholder="Clínica Vitali"
						fullWidth
						variant="outlined"
						error={!!apiValidationErrors?.businessName}
						helperText={apiValidationErrors?.businessName}
						InputProps={{
							classes: {
								root: classes.inputRoot,
								focused: classes.inputFocused
							}
						}}
						className={classes.fullWidthField}
					/>
					<TextField
						type="text"
						value={formData.contactName}
						onChange={(event) => handleFormFieldChange("contactName", event.target.value)}
						placeholder="Camila"
						fullWidth
						variant="outlined"
						error={!!apiValidationErrors?.contactName}
						helperText={apiValidationErrors?.contactName}
						InputProps={{
							classes: {
								root: classes.inputRoot,
								focused: classes.inputFocused
							}
						}}
					/>
					<Grid container direction="column">
						<Grid item>
							<Box className={classes.phoneWrapper}>
								<TextField
									type="tel"
									value={formData.whatsapp}
									onChange={(event) => handleFormFieldChange("whatsapp", keepOnlyDigits(event.target.value))}
									placeholder="(31) 99999-9999"
									fullWidth
									variant="outlined"
									InputProps={{
										classes: {
											root: phoneInputRootClassName,
											focused: classes.inputFocused
										}
									}}
								/>
							</Box>
						</Grid>
						{apiValidationErrors?.whatsapp && (
							<Grid item>
								<Typography className={classes.errorText}>
									{apiValidationErrors?.whatsapp}
								</Typography>
							</Grid>
						)}
					</Grid>
				</Box>
				<Box className={classes.dividerSection}>
					<Typography className={classes.dividerLabel}>
						{"ONDE QUERO INVESTIR"}
					</Typography>
					<Box className={classes.dividerLine} />
				</Box>
				<Box className={classes.optionsGrid}>
					{renderChannelOption("google_ads")}
					{renderChannelOption("meta_ads")}
				</Box>
				<Typography className={classes.helperText}>
					{"Você acompanha da negociação até a liberação da verba"}
				</Typography>
			</React.Fragment>
		)
	}

	const renderAttendantForm = (): React.ReactNode => {
		return (
			<React.Fragment>
				<Typography className={classes.fieldLabel}>
					{"Quem foi a última pessoa que te perguntou como você organiza seu WhatsApp?"}
				</Typography>
				<Box className={classes.fieldsGrid}>
					<TextField
						type="text"
						value={formData.companyName}
						onChange={(event) => handleFormFieldChange("companyName", event.target.value)}
						placeholder="Camila, da Clínica Vitali"
						fullWidth
						variant="outlined"
						error={!!apiValidationErrors?.businessName}
						helperText={apiValidationErrors?.businessName}
						InputProps={{
							classes: {
								root: classes.inputRoot,
								focused: classes.inputFocused
							}
						}}
					/>
					<Grid container direction="column">
						<Grid item>
							<Box className={classes.phoneWrapper}>
								<TextField
									type="tel"
									value={formData.whatsapp}
									onChange={(event) => handleFormFieldChange("whatsapp", keepOnlyDigits(event.target.value))}
									placeholder="(31) 99999-9999"
									fullWidth
									variant="outlined"
									InputProps={{
										classes: {
											root: phoneInputRootClassName,
											focused: classes.inputFocused
										}
									}}
								/>
							</Box>
						</Grid>
						{apiValidationErrors?.whatsapp && (
							<Grid item>
								<Typography className={classes.errorText}>
									{apiValidationErrors?.whatsapp}
								</Typography>
							</Grid>
						)}
					</Grid>
				</Box>
				<Box className={classes.dividerSection}>
					<Typography className={classes.dividerLabel}>
						{"QUERO RECEBER"}
					</Typography>
					<Box className={classes.dividerLine} />
				</Box>
				<Box className={classes.optionsGrid}>
					{renderPayoutMethodOption("cash")}
					{renderPayoutMethodOption("invoice_discount")}
				</Box>
				<Typography className={classes.helperText}>
					{"Você acompanha da negociação até a liberação do bônus"}
				</Typography>
			</React.Fragment>
		)
	}

	const submitButtonLabel = isAdmin ? "Liberar" : "Liberar meu resgate"
	const submitButtonClassName = isSubmitting
		? `${classes.submitButton} ${classes.submitButtonDisabled}`
		: classes.submitButton

	return (
		<Box>
			<Box className={classes.header}>
				<Box className={classes.badge}>
					<Box className={classes.badgeIcon}>{"R$"}</Box>
					<Typography component="span">{"Indique e ganhe"}</Typography>
				</Box>
				<Typography className={classes.title}>
					{isAdmin ? "Falta um nome." : "Você está a um passo do seu resgate"}
				</Typography>
				<Typography className={classes.subtitle}>
					{isAdmin
						? "Indique uma empresa que precisa de uma plataforma como a Letalk. Quando ela fechar com a gente, marcamos sua call e liberamos a verba."
						: "Indique uma empresa que precisa organizar o WhatsApp. Quando ela fechar, os R$ 200 são seus — em dinheiro ou como desconto na fatura, você escolhe."}
				</Typography>
			</Box>
			<Box className={classes.formCard}>
				{isAdmin ? renderAdminForm() : renderAttendantForm()}
			</Box>
			<Box className={classes.footer}>
				<Button
					type="button"
					className={classes.dontRememberLink}
					onClick={handleDontRememberClick}
					disableRipple
				>
					{"Não lembro agora →\nme manda meu link"}
				</Button>
				<Button
					type="button"
					className={submitButtonClassName}
					onClick={handleSubmitClick}
					disabled={isSubmitting}
					disableElevation
				>
					{submitButtonLabel}
				</Button>
			</Box>
		</Box>
	)
}

export default ReferralFormScreen
